import { atom } from 'jotai';
import { AIScriptParams, AIScriptResponse, ScriptTone } from '../services/aiScriptService';

/**
 * AI Script Generation State Atoms
 * Manages AI-generated scripts, regeneration, and delivery coaching
 */

// Generated Script State
export const generatedScriptAtom = atom<AIScriptResponse | null>(null);
export const isGeneratingScriptAtom = atom<boolean>(false);
export const scriptGenerationErrorAtom = atom<string | null>(null);

// Script Input Parameters
export const scriptTopicAtom = atom<string>('');
export const scriptToneAtom = atom<ScriptTone>('professional');
export const scriptDurationAtom = atom<number>(60); // seconds

// Script Editing State
export const isEditingScriptAtom = atom<boolean>(false);
export const editedScriptAtom = atom<string>('');

// Regeneration State
export const regenerationFeedbackAtom = atom<string>('');
export const isRegeneratingAtom = atom<boolean>(false);

// Delivery Coaching Status
export type CoachingStatus = 
  | 'idle'           // Coaching disabled or not started
  | 'awaiting'       // Enabled but recording not started
  | 'listening'      // Listening for speech
  | 'processing'     // Speech detected, analyzing
  | 'networkError';  // Network connectivity issue

// Delivery Coaching State
export interface DeliveryCoachingState {
  isActive: boolean;
  status: CoachingStatus;
  currentPace: number; // WPM
  paceStatus: 'good' | 'too-fast' | 'too-slow';
  fillerWordCount: number;
  totalWords: number;
  suggestions: string[];
  transcript: string;
  lastUpdated: number;
}

export const deliveryCoachingAtom = atom<DeliveryCoachingState>({
  isActive: false,
  status: 'idle',
  currentPace: 0,
  paceStatus: 'good',
  fillerWordCount: 0,
  totalWords: 0,
  suggestions: [],
  transcript: '',
  lastUpdated: Date.now()
});

// Coaching Toggle
export const isCoachingEnabledAtom = atom<boolean>(false);

// Derived Atoms
export const hasGeneratedScriptAtom = atom((get) => {
  const script = get(generatedScriptAtom);
  return script !== null && script.script.length > 0;
});

export const scriptWordCountAtom = atom((get) => {
  const script = get(generatedScriptAtom);
  return script?.wordCount || 0;
});

export const fillerWordRateAtom = atom((get) => {
  const coaching = get(deliveryCoachingAtom);
  if (coaching.totalWords === 0) return 0;
  return Math.round((coaching.fillerWordCount / coaching.totalWords) * 100);
});

// Action Atoms
export const resetScriptStateAtom = atom(null, (get, set) => {
  set(generatedScriptAtom, null);
  set(isGeneratingScriptAtom, false);
  set(scriptGenerationErrorAtom, null);
  set(scriptTopicAtom, '');
  set(isEditingScriptAtom, false);
  set(editedScriptAtom, '');
  set(regenerationFeedbackAtom, '');
  set(isRegeneratingAtom, false);
});

export const updateCoachingMetricsAtom = atom(
  null,
  (get, set, update: Partial<DeliveryCoachingState>) => {
    const current = get(deliveryCoachingAtom);
    set(deliveryCoachingAtom, {
      ...current,
      ...update,
      lastUpdated: Date.now()
    });
  }
);
